/**
 * Policy-driven gate for ESS / My Team / Admin attendance request types.
 * Matches the employee's attendance process policy (category/org applicability).
 */

import { normalizePolicyRecord } from "./attendancePolicyConfig";
import {
  loadAttendancePolicies,
  resolvePolicyForEmployee,
} from "./attendancePolicyEngine";
import { isChangeWoEnabledForEmployee } from "./weeklyOffChange";

/**
 * @returns {{
 *   policy: object|null,
 *   changeShift: boolean,
 *   changeWo: boolean,
 *   regularise: boolean,
 *   shortLeave: boolean,
 *   leave: boolean,
 *   od: boolean,
 *   ot: boolean,
 *   compOff: boolean,
 * }}
 */
export function resolveEmployeeAttendanceRequestAccess(
  employee,
  asOfDate = new Date(),
  policies = null
) {
  const policyList = policies ?? loadAttendancePolicies();
  const raw = employee
    ? resolvePolicyForEmployee(employee, policyList, asOfDate)
    : null;
  const policy = normalizePolicyRecord(raw);
  const changeWo = isChangeWoEnabledForEmployee(employee);

  if (!policy) {
    return {
      policy: null,
      changeShift: true,
      changeWo,
      regularise: true,
      shortLeave: false,
      leave: true,
      od: false,
      ot: false,
      compOff: false,
    };
  }

  return {
    policy,
    changeShift: policy.changeShiftEnabled !== false,
    changeWo,
    regularise: policy.regularizationAllowed !== false,
    shortLeave: !!policy.shortLeaveEnabled,
    leave: true,
    od: policy.odRequestEnabled !== false,
    ot: !!policy.overtimeEnabled,
    compOff:
      !!policy.compensatoryOffEnabled ||
      policy.otCompOffMode === "comp_off_only" ||
      policy.otCompOffMode === "both",
  };
}

/** Map REQUEST_TYPE_DEFS key → access boolean. */
export function isRequestTypeAllowedByPolicy(requestTypeKey, access) {
  if (!access) return false;
  switch (requestTypeKey) {
    case "change_shift":
      return !!access.changeShift;
    case "change_wo":
      return !!access.changeWo;
    case "regularization_checks":
      return !!access.regularise;
    case "short_leave":
      return !!access.shortLeave;
    case "leave_request":
      return !!access.leave;
    case "od":
    case "on_duty":
      return !!access.od;
    case "ot":
    case "overtime":
      return !!access.ot;
    case "comp_off":
      return !!access.compOff;
    case "resignation":
    case "ticket_request":
    case "tax_declaration":
    case "mrf_request":
      return true;
    default:
      return true;
  }
}

/** Minimal employee stub for policy matching from auth / desk context. */
export function employeeStubFromIds({ employeeId, ...rest } = {}) {
  if (employeeId == null || employeeId === "") return null;
  return {
    id: employeeId,
    employeeId,
    ...rest,
  };
}
